/**
 * Processing Settings Component
 *
 * Configures processing mode (batch vs real-time) and provider settings
 * for feed import operations. Super admin only.
 *
 * Part of Story: Multi-Provider Processing Mode for Feed Imports
 * Date: 2026-03-06
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Settings,
  Zap,
  Clock,
  DollarSign,
  Info,
  Save,
  RefreshCw,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { authFetch } from '@/utils/auth-fetch';
import { useAuth } from '@/context/auth-context';

interface ProcessingConfig {
  mode: 'batch' | 'realtime';
  enrichment_provider: 'openai' | 'vertexai';
  enrichment_model: string;
  batch_size: {
    embedding: number;
    enrichment: number;
  };
}

interface CostEstimate {
  batch: {
    embedding: number;
    enrichment: number;
    benefit_scoring: number;
    total: number;
  };
  realtime: {
    embedding: number;
    enrichment: number;
    benefit_scoring: number;
    total: number;
  };
  productCount: number;
}

interface ProcessingSettingsProps {
  siteId: string;
}

const PROVIDER_OPTIONS = [
  {
    value: 'openai',
    label: 'OpenAI',
    description: 'GPT-4o-mini, GPT-4o',
  },
  {
    value: 'vertexai',
    label: 'Google Vertex AI',
    description: 'Gemini 2.0 Flash, Gemini 1.5 Pro',
  },
];

const MODEL_OPTIONS: Record<string, Array<{ value: string; label: string; description: string }>> = {
  openai: [
    { value: 'gpt-4o-mini', label: 'GPT-4o Mini', description: 'Fast & cost-effective' },
    { value: 'gpt-4o', label: 'GPT-4o', description: 'Higher quality, higher cost' },
  ],
  vertexai: [
    { value: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash', description: 'Fast & cost-effective' },
    { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash', description: 'Premium quality' },
    { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro', description: 'Top quality - Highest reasoning depth' },
    { value: 'gemini-1.5-pro', label: 'Gemini 1.5 Pro', description: 'Legacy' },
  ],
};

const DEFAULT_CONFIG: ProcessingConfig = {
  mode: 'batch',
  enrichment_provider: 'openai',
  enrichment_model: 'gpt-4o-mini',
  batch_size: {
    embedding: 500,
    enrichment: 100,
  },
};

export function ProcessingSettings({ siteId }: ProcessingSettingsProps) {
  const [config, setConfig] = useState<ProcessingConfig>(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [estimating, setEstimating] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [costEstimate, setCostEstimate] = useState<CostEstimate | null>(null);
  const { toast } = useToast();
  const { user } = useAuth();

  const isSuperAdmin = user?.role === 'super_admin';

  // Load current configuration
  useEffect(() => {
    if (siteId) {
      loadConfig();
    }
  }, [siteId]);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await authFetch(`/sites/${siteId}/processing-config`);
      if (response.ok) {
        const data = await response.json();
        if (data.config) {
          setConfig({
            ...DEFAULT_CONFIG,
            ...data.config,
            batch_size: {
              ...DEFAULT_CONFIG.batch_size,
              ...(data.config.batch_size || {}),
            },
          });
        }
      }
    } catch (error) {
      console.error('Error loading processing config:', error);
      toast({
        title: 'Error',
        description: 'Failed to load processing configuration',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Estimate costs when config changes
  const estimateCosts = useCallback(async () => {
    if (!siteId) return;

    try {
      setEstimating(true);
      const response = await authFetch(`/sites/${siteId}/processing-config/estimate`, {
        method: 'POST',
        body: JSON.stringify({ config }),
      });

      if (response.ok) {
        const data = await response.json();
        setCostEstimate(data.estimate);
      }
    } catch (error) {
      console.error('Error estimating costs:', error);
    } finally {
      setEstimating(false);
    }
  }, [siteId, config]);

  useEffect(() => {
    if (!loading && siteId) {
      const timer = setTimeout(estimateCosts, 500);
      return () => clearTimeout(timer);
    }
  }, [config, loading, siteId, estimateCosts]);

  // Save configuration
  const handleSave = async () => {
    if (!isSuperAdmin) {
      toast({
        title: 'Access Denied',
        description: 'Only super admins can modify processing settings',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    try {
      const response = await authFetch(`/sites/${siteId}/processing-config`, {
        method: 'PUT',
        body: JSON.stringify({ config }),
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Processing settings updated successfully',
        });
        setHasChanges(false);
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save');
      }
    } catch (error) {
      console.error('Error saving processing config:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save processing settings',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle config changes
  const updateConfig = (updates: Partial<ProcessingConfig>) => {
    setConfig(prev => ({ ...prev, ...updates }));
    setHasChanges(true);
  };

  const updateBatchSize = (key: 'embedding' | 'enrichment', value: number) => {
    setConfig(prev => ({
      ...prev,
      batch_size: { ...prev.batch_size, [key]: value },
    }));
    setHasChanges(true);
  };

  // Handle provider change - reset model to default for new provider
  const handleProviderChange = (provider: 'openai' | 'vertexai') => {
    const defaultModel = MODEL_OPTIONS[provider][0].value;
    updateConfig({
      enrichment_provider: provider,
      enrichment_model: defaultModel
    });
  };

  if (!isSuperAdmin) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Processing Settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Processing settings are only accessible to super admins.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-96" />
        </CardHeader>
        <CardContent className="space-y-6">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Processing Settings
            </CardTitle>
            <CardDescription>
              Configure how feed imports are processed. These settings affect enrichment, benefit scoring, and embedding generation.
            </CardDescription>
          </div>
          {hasChanges && (
            <Badge variant="secondary" className="animate-pulse">
              Unsaved changes
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-8">
        {/* Processing Mode */}
        <div className="space-y-4">
          <Label className="text-base font-semibold">Processing Mode</Label>
          <RadioGroup
            value={config.mode}
            onValueChange={(value: 'batch' | 'realtime') => updateConfig({ mode: value })}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div
                className={`relative p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  config.mode === 'batch'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                }`}
                onClick={() => updateConfig({ mode: 'batch' })}
              >
                <div className="flex items-start gap-3">
                  <RadioGroupItem value="batch" id="batch" className="mt-1" />
                  <div className="flex-1">
                    <Label htmlFor="batch" className="cursor-pointer">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        <span className="font-semibold">Batch Processing</span>
                        <Badge variant="outline" className="text-xs">Recommended</Badge>
                      </div>
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      50% cost savings, 7-11 hours for large imports
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <DollarSign className="h-3 w-3 text-green-600" />
                        Lower cost
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Slower
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div
                className={`relative p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  config.mode === 'realtime'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                }`}
                onClick={() => updateConfig({ mode: 'realtime' })}
              >
                <div className="flex items-start gap-3">
                  <RadioGroupItem value="realtime" id="realtime" className="mt-1" />
                  <div className="flex-1">
                    <Label htmlFor="realtime" className="cursor-pointer">
                      <div className="flex items-center gap-2">
                        <Zap className="h-4 w-4" />
                        <span className="font-semibold">Real-time Processing</span>
                      </div>
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      2x cost, ~1 hour for large imports (10x faster)
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <DollarSign className="h-3 w-3 text-amber-600" />
                        Higher cost
                      </span>
                      <span className="flex items-center gap-1">
                        <Zap className="h-3 w-3 text-green-600" />
                        Faster
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </RadioGroup>
        </div>

        {/* Provider & Model Selection */}
        <div className="space-y-4 border-t pt-6">
          <Label className="text-base font-semibold">Enrichment Provider & Model</Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="provider">Provider</Label>
              <Select
                value={config.enrichment_provider}
                onValueChange={(value: 'openai' | 'vertexai') => handleProviderChange(value)}
              >
                <SelectTrigger id="provider">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PROVIDER_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      <div>
                        <div className="font-medium">{option.label}</div>
                        <div className="text-xs text-muted-foreground">{option.description}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="model">Model</Label>
              <Select
                value={config.enrichment_model}
                onValueChange={(value) => updateConfig({ enrichment_model: value })}
              >
                <SelectTrigger id="model">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MODEL_OPTIONS[config.enrichment_provider]?.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      <div>
                        <div className="font-medium">{option.label}</div>
                        <div className="text-xs text-muted-foreground">{option.description}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Embeddings Info */}
        <div className="space-y-4 border-t pt-6">
          <Label className="text-base font-semibold">Embeddings</Label>
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              <strong>Model:</strong> text-embedding-3-large (OpenAI)
              <br />
              <span className="text-muted-foreground">
                Embedding model is system-wide and cannot be changed per-site to ensure consistent vector search.
              </span>
            </AlertDescription>
          </Alert>
        </div>

        {/* Batch Size Configuration (only for batch mode) */}
        {config.mode === 'batch' && (
          <div className="space-y-6 border-t pt-6">
            <Label className="text-base font-semibold">Batch Sizes</Label>
            <p className="text-sm text-muted-foreground">
              Larger batches reduce API overhead. DB operations remain chunked at 50 records for safety.
            </p>

            <div className="space-y-6">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label htmlFor="embedding-batch">Embedding batch size</Label>
                  <span className="text-sm font-mono bg-muted px-2 py-1 rounded">
                    {config.batch_size.embedding.toLocaleString()} records
                  </span>
                </div>
                <Slider
                  id="embedding-batch"
                  value={[config.batch_size.embedding]}
                  onValueChange={([value]) => updateBatchSize('embedding', value)}
                  min={50}
                  max={5000}
                  step={50}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>50</span>
                  <span>5,000</span>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label htmlFor="enrichment-batch">Enrichment batch size</Label>
                  <span className="text-sm font-mono bg-muted px-2 py-1 rounded">
                    {config.batch_size.enrichment.toLocaleString()} records
                  </span>
                </div>
                <Slider
                  id="enrichment-batch"
                  value={[config.batch_size.enrichment]}
                  onValueChange={([value]) => updateBatchSize('enrichment', value)}
                  min={50}
                  max={2000}
                  step={50}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>50</span>
                  <span>2,000</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Cost Estimate */}
        {costEstimate && (
          <div className="space-y-4 border-t pt-6">
            <div className="flex items-center gap-2">
              <Label className="text-base font-semibold">Cost Estimate</Label>
              {estimating && <Loader2 className="h-4 w-4 animate-spin" />}
            </div>
            <p className="text-sm text-muted-foreground">
              Estimated costs for {costEstimate.productCount.toLocaleString()} products
            </p>

            <div className="grid grid-cols-2 gap-4">
              <div className={`p-4 rounded-lg border ${config.mode === 'batch' ? 'border-primary bg-primary/5' : 'border-border'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="h-4 w-4" />
                  <span className="font-medium">Batch Mode</span>
                  {config.mode === 'batch' && <Badge variant="secondary" className="text-xs">Selected</Badge>}
                </div>
                <div className="text-2xl font-bold text-green-600">
                  ${costEstimate.batch.total.toFixed(2)}
                </div>
                <div className="text-xs text-muted-foreground mt-1 space-y-1">
                  <div>Embedding: ${costEstimate.batch.embedding.toFixed(2)}</div>
                  <div>Enrichment: ${costEstimate.batch.enrichment.toFixed(2)}</div>
                  <div>Benefit scoring: ${costEstimate.batch.benefit_scoring.toFixed(2)}</div>
                </div>
              </div>

              <div className={`p-4 rounded-lg border ${config.mode === 'realtime' ? 'border-primary bg-primary/5' : 'border-border'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="h-4 w-4" />
                  <span className="font-medium">Real-time Mode</span>
                  {config.mode === 'realtime' && <Badge variant="secondary" className="text-xs">Selected</Badge>}
                </div>
                <div className="text-2xl font-bold text-amber-600">
                  ${costEstimate.realtime.total.toFixed(2)}
                </div>
                <div className="text-xs text-muted-foreground mt-1 space-y-1">
                  <div>Embedding: ${costEstimate.realtime.embedding.toFixed(2)}</div>
                  <div>Enrichment: ${costEstimate.realtime.enrichment.toFixed(2)}</div>
                  <div>Benefit scoring: ${costEstimate.realtime.benefit_scoring.toFixed(2)}</div>
                </div>
              </div>
            </div>

            <p className="text-xs text-muted-foreground">
              * Estimates based on average token usage. Actual costs may vary.
            </p>
          </div>
        )}

        {/* Save Button */}
        <div className="flex justify-end pt-4 border-t">
          <Button onClick={handleSave} disabled={saving || !hasChanges}>
            {saving ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Processing Settings
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
